import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { EDIT_SELECTOR } from "../../../../../crossconcern/utilities/properties/selector.property";
import { BaseComponent } from "../../../../base/base.component";
import { AppComponent } from "../../../../app.component";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { NurseryManager } from "../../../../../business/managers/nursery.manager";
import { concatMap, filter, finalize, tap } from "rxjs/operators";
import { NurseryModel } from "../../../../../datastore/entities/nursery.model";
import { FormInfo } from "../../../../../crossconcern/models/form/forminfo";
import {
    EDIT_MODE, FORM_TYPE_CHECKBOX, FORM_TYPE_FILE,
    FORM_TYPE_TEXT,
    FORM_WIDTH_COL_4,
    FORM_WIDTH_OFFSET_COL_0
} from "../../../../../crossconcern/utilities/properties/base.property";
import { FormInfoField } from "../../../../../crossconcern/models/form/forminfofield";
import { Validators } from "@angular/forms";
import { AlertUtils } from "../../../../../crossconcern/utilities/utils/alert.utils";
import {
    PRIVATE_NURSERY_DETAIL_ROUTE
} from "../../../../../crossconcern/utilities/properties/routename.property";
import { Observable, of, Subscription } from "rxjs";
import { GenericService } from "../../../../../crossconcern/services/generic.services";
import { HttpEventType, HttpResponse } from "@angular/common/http";

@Component({
    selector: EDIT_SELECTOR,
    templateUrl: "../detail/nurserydetail.component.html"
})
export class NurseryEditComponent extends BaseComponent implements OnInit, OnDestroy {

    public nursery: NurseryModel;
    public formInfo: FormInfo;

    public selectedFiles: FileList[] = [];

    @ViewChild("formNursery", null) private formComponent;

    private onGoBackCalledSubscription: Subscription;

    public mode = EDIT_MODE;

    public imageURL: string = null;

    public sphereLoadingVisible: boolean;
    public currentLoading: string;

    constructor(
        protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        protected genericService: GenericService,
        private nurseryManager: NurseryManager,
        private activeRoute: ActivatedRoute,
        protected alertUtils: AlertUtils) {
        super(appComponent, router, alertUtils, location);
        const formInfoFields: FormInfoField[] = [
            new FormInfoField(
                FORM_TYPE_TEXT,
                "NURSERY_DETAIL_COMPONENT_NAME",
                "name",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_TEXT,
                "NURSERY_DETAIL_COMPONENT_ADDRESS",
                "address",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_TEXT,
                "NURSERY_DETAIL_COMPONENT_LAT",
                "latitude",
                [
                    Validators.required,
                    Validators.pattern("[0-9]+([.][0-9]+)?")
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_TEXT,
                "NURSERY_DETAIL_COMPONENT_LONG",
                "longitude",
                [
                    Validators.required,
                    Validators.pattern("[0-9]+([.][0-9]+)?")
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_FILE,
                "NURSERY_DETAIL_COMPONENT_SPHERES",
                "spheres",
                [],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0,
                undefined,
                undefined,
                ".jpg,.jpeg,.png",
                true),
            new FormInfoField(
                FORM_TYPE_FILE,
                "NURSERY_DETAIL_COMPONENT_CSV",
                "csv",
                [],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0,
                undefined,
                undefined,
                ".csv",
                false),
            new FormInfoField(
                FORM_TYPE_TEXT,
                "NURSERY_DETAIL_COMPONENT_VERSION_NUMBER",
                "versionNumber",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_CHECKBOX,
                "NURSERY_DETAIL_COMPONENT_VISIBLE",
                "visible",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                false,
                null,
                FORM_WIDTH_COL_4,
                FORM_WIDTH_OFFSET_COL_0)
        ];

        this.formInfo = new FormInfo(formInfoFields, null);

        this.subscribeGenericServices();
    }

    public ngOnInit(): void {
        this.activeRoute.params.subscribe(
            (params) => {
                this.getNursery(params.id);
            }
        );
    }

    public ngOnDestroy() {
        this.unsubscribeServices();
    }

    public getNursery(id: string) {
        this.appComponent.startLoading();
        this.nurseryManager.getNursery(id).pipe(
            finalize(() => this.appComponent.stopLoading())
        ).subscribe(
            (nursery) => {
                this.nursery = nursery;
                const values = {
                    name: this.nursery.name,
                    address: this.nursery.address,
                    latitude: this.nursery.latitude,
                    longitude: this.nursery.longitude,
                    visible: (this.nursery.visible === null) ? false : this.nursery.visible,
                    versionNumber: this.nursery.versionNumber,
                    spheres: null,
                    csv: null
                };
                for (const field of this.formInfo.fields) {
                    field.selectedValue = values[field.formControlName];
                }
                this.formComponent.updateFormValues(this.formInfo);
            });
    }

    public onSaveNursery() {
        this.appComponent.startLoading();
        this.nurseryManager.editNursery(
            this.nursery.uuid,
            {
                "name": this.formComponent.form.value.name,
                "address": this.formComponent.form.value.address,
                "latitude": this.formComponent.form.value.latitude,
                "longitude": this.formComponent.form.value.longitude,
                "visible": this.formComponent.form.value.visible,
                "version_number": this.formComponent.form.value.versionNumber
            })
            .pipe(
                concatMap(() => this.uploadImages()),
                concatMap(() => this.uploadCSV()),
                finalize(() => this.appComponent.stopLoading())
            )
            .subscribe({
                complete: () => this.goToRouteByName(PRIVATE_NURSERY_DETAIL_ROUTE, { id: this.nursery.uuid })
            });

    }

    public onFilesSelected(event) {
        this.selectedFiles = event;
    }

    /* eslint-disable dot-notation */
    public uploadImages(): Observable<{}> {
        if(this.selectedFiles["spheres"] && this.selectedFiles["spheres"].length > 0) {
            this.showSpheresProgress("0%");
            return this.nurseryManager.addNurserySpheres(this.selectedFiles["spheres"], this.nursery.uuid)
                .pipe(
                    tap((event) => {
                        if(event && event["type"] === HttpEventType.UploadProgress) {
                            const percentDone = Math.round(100 * event["loaded"] / event["total"]);
                            if(percentDone < 100) {
                                this.showSpheresProgress(percentDone + "%");
                            } else {
                                this.showSpheresProgress("NURSERY_EDIT_COMPONENT_LOADING_CONNECT");
                            }
                        }
                    }),
                    filter((event) => event instanceof HttpResponse),
                    finalize(() => this.hideSpheresProgress())
                );
        }
        return of({});
    }

    public uploadCSV(): Observable<{}> {
        if(this.selectedFiles["csv"] && this.selectedFiles["csv"].length > 0) {
            return this.nurseryManager.addNurseryCSV(this.selectedFiles["csv"], this.nursery.uuid);
        }
        return of({});
    }
    /* eslint-enable dot-notation */

    private subscribeGenericServices() {
        this.onGoBackCalledSubscription = this.genericService.onGoBackCalled.subscribe(() => {
            this.goToRouteByName(PRIVATE_NURSERY_DETAIL_ROUTE, { id: this.nursery.uuid });
        });
    }

    private unsubscribeServices() {
        if(this.onGoBackCalledSubscription) {
            this.onGoBackCalledSubscription.unsubscribe();
        }
    }

    private showSpheresProgress(text: string) {
        this.sphereLoadingVisible = true;
        this.currentLoading = text;
    }

    private hideSpheresProgress() {
        this.sphereLoadingVisible = false;
        this.currentLoading = "0%";
    }
}